import React, { useEffect, useMemo } from 'react';
import { Set } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import {
  contactNotesSelector,
  currentlyEditingNoteSelector,
  deleteNote,
  setCurrentlyEditingNote,
} from '@repo/redux/actions/notes';
import { ID } from '@repo/utils/types';
import {
  creatingNewNoteInContactViewSelector,
  setCreatingNewNoteInContactView,
} from '@repo/redux/actions/ui';
import Contact, { RelatedContact } from '@repo/utils/models/Contact';
import Note from '@repo/utils/models/Note';
import { TimelineItem, TimelineFilter } from '@repo/utils/models/Timeline';
import {
  selectContactTimeline,
  selectFilteredContactTimeline,
} from '@repo/redux/reducers/data/timeline';
import { fetchFilteredContactTimeline } from '@repo/redux/actions/timeline';
import { TimelineItemsList } from '@repo/components/timeline';

export default function Timeline(props: {
  contact: Contact;
  relatedContacts: RelatedContact[];
  setRelatedContacts: (relatedContacts: RelatedContact[]) => void;
  filterValue: TimelineFilter | null;
}) {
  const dispatch = useDispatch();
  const notes = useSelector(contactNotesSelector(props.contact.id));
  const timeline = useSelector(selectContactTimeline(props.contact.id));
  const filteredTimeline = useSelector(
    selectFilteredContactTimeline(props.contact.id, props.filterValue),
  );
  const currentlyEditingNote = useSelector(currentlyEditingNoteSelector);
  const creatingNewNote = useSelector(creatingNewNoteInContactViewSelector);

  const timelineItems = useMemo<TimelineItem[]>(() => {
    if (props.filterValue) {
      return filteredTimeline?.items?.valueSeq().toArray() || [];
    }

    return timeline.items.valueSeq().toArray();
  }, [timeline, filteredTimeline, props.filterValue]);

  const relatedContactIDs = useMemo<Set<ID>>(
    () => Set(props.relatedContacts.map(({ id }) => id)),
    [props.relatedContacts],
  );

  const externallyRelatedContactIDs = useMemo<Set<ID>>(
    () =>
      Set(props.relatedContacts.filter(({ bidirectional }) => bidirectional).map(({ id }) => id)),
    [props.relatedContacts],
  );

  // Set of the contact mention IDs in all of the available notes except the note that is currently being
  // edited. If we are editing a persisted note, we are operating on a copy as currentlyEditingNote -- we filter
  // the original out of notes so that we can accurately determine if the full set of mentions has changed
  // when a user edits the currentlyEditingNote's mentions.
  const mentionsIDsInReadOnlyNotes = useMemo<Set<ID>>(() => {
    const filteredNotes = currentlyEditingNote
      ? notes.filter((note) => note.id != currentlyEditingNote.id)
      : notes;
    return filteredNotes.reduce((ids, note) => ids.concat(note.mentions), Set<ID>()).toSet();
  }, [notes, currentlyEditingNote?.id]);

  const setRelatedContactsIfChanged = (mentionIDs: Set<ID>) => {
    // We add the related contacts from elsewhere when determining the new full set
    // of related contacts when a note has been edited
    const combinedMentionIDs = mentionIDs.union(externallyRelatedContactIDs);

    if (!relatedContactIDs.equals(combinedMentionIDs)) {
      const addedMentions = combinedMentionIDs
        .subtract(relatedContactIDs)
        .valueSeq()
        .toArray()
        .map((id) => ({ id } as RelatedContact));
      // filter out deletions
      const newRelatedContacts = props.relatedContacts
        .filter(({ id }) => combinedMentionIDs.has(id))
        .concat(addedMentions);
      props.setRelatedContacts(newRelatedContacts);
    }
  };

  useEffect(() => {
    if (currentlyEditingNote) {
      const mentionsIDsInNotes = Set<ID>(
        mentionsIDsInReadOnlyNotes.concat(currentlyEditingNote.mentions),
      );
      setRelatedContactsIfChanged(mentionsIDsInNotes);
    }
  }, [currentlyEditingNote]);

  // If a note is deleted, reset the mentions for this Contact if needed
  useEffect(() => {
    if (props.contact.updated) {
      setRelatedContactsIfChanged(mentionsIDsInReadOnlyNotes);
    }
  }, [notes.length]);

  useEffect(() => {
    if (!currentlyEditingNote && creatingNewNote) {
      dispatch(setCreatingNewNoteInContactView(false));
    }
  }, [currentlyEditingNote]);

  useEffect(() => {
    if (props.filterValue && !filteredTimeline) {
      dispatch(
        fetchFilteredContactTimeline({
          contactID: props.contact.id,
          filterType: props.filterValue,
        }),
      );
    }
  }, [props.filterValue]);

  // when component unmounts, ensure we clear the currentlyEditing state
  useEffect(
    () => () => {
      dispatch(setCurrentlyEditingNote(null));
    },
    [],
  );

  // selection for editing
  const setCurrentlyEditing = (note, e) => {
    if (e) {
      e.preventDefault();
      // had to add this because of (in theory) changes in focus/blur in React 17.
      // the blur() even was being called immediately after, resulting in the note
      // never being editable.
      e.stopPropagation();
    }

    // If a user clicks straight from a focused new note to an existing note,
    // the blur handler won't get called (due to the e.stopPropagation above).
    // If that happens, we need to manually clear the new note state to prevent
    // a duplicate note appearing.
    if (note?.isPersisted && creatingNewNote) {
      dispatch(setCreatingNewNoteInContactView(false));
    }

    dispatch(setCurrentlyEditingNote(note));
  };

  const clearCurrentlyEditing = () => setCurrentlyEditing(null, null);

  const clearNewNote = (note: Note) => {
    // if the new note is persisted AND it is empty, delete it here
    if (note.isPersisted && note.isEmpty) {
      dispatch(deleteNote(note));
    }

    dispatch(setCreatingNewNoteInContactView(false));
    clearCurrentlyEditing();
  };

  const loadMoreFilteredTimeline = () => {
    dispatch(
      fetchFilteredContactTimeline({ contactID: props.contact.id, filterType: props.filterValue }),
    );
  };

  return (
    <TimelineItemsList
      contact={props.contact}
      timelineItems={timelineItems}
      showWindowButtonAfterID={timeline.showBtnAfterID}
      currentlyEditingNote={currentlyEditingNote}
      clearNewNote={clearNewNote}
      deleteNote={(note) => dispatch(deleteNote(note))}
      setCurrentlyEditing={setCurrentlyEditing}
      clearCurrentlyEditing={clearCurrentlyEditing}
      filterApplied={!!props.filterValue}
      loadMoreFilteredTimeline={
        !!props.filterValue && filteredTimeline?.canLoadMore ? loadMoreFilteredTimeline : undefined
      }
      loadingFilteredTimeline={!!props.filterValue && filteredTimeline?.isFetching}
    />
  );
}
